/**
 * overlay.js — Pure read-time overlay functions for OVERLAY-layer mutations.
 *
 * These functions merge pending mutations from the mutation log into server
 * payloads so the UI reflects optimistic state without waiting for a round-trip.
 *
 * Exported: overlayDaily, overlayTopicDigest.
 * NOT exported: overlayArticle (dropped per design — article detail fetches
 * fresh data; overlay is only needed for list/briefing views).
 */

/**
 * Build read/unread sets from pending mark_read / mark_unread / mark_all_read
 * mutations. Last write wins per article id (mutation order = insertion order).
 */
function buildReadOverlay(pendingMutations) {
  const reads = new Set();
  const unreads = new Set();
  for (const m of pendingMutations) {
    if (m.type === 'mark_read' && m.payload?.article_id != null) {
      reads.add(m.payload.article_id);
      unreads.delete(m.payload.article_id);
    } else if (m.type === 'mark_unread' && m.payload?.article_id != null) {
      unreads.add(m.payload.article_id);
      reads.delete(m.payload.article_id);
    } else if (m.type === 'mark_all_read' && Array.isArray(m.payload?.article_ids)) {
      for (const id of m.payload.article_ids) {
        reads.add(id);
        unreads.delete(id);
      }
    }
  }
  return { reads, unreads };
}

function applyReadFlag(article, reads, unreads) {
  if (article == null) return article;
  // Reason: whats_new items (daily briefing) use source_article_id; topic
  // articles use id. Support both so one function covers both surfaces.
  const id = article.id ?? article.source_article_id;
  if (id == null) return article;
  if (reads.has(id)) return article.is_read ? article : { ...article, is_read: true };
  if (unreads.has(id)) return article.is_read ? { ...article, is_read: false } : article;
  return article;
}

/**
 * Overlay pending read mutations onto a daily briefing payload.
 *
 * @param {object|null} briefing  Server briefing payload (has `.themes[].whats_new[]`).
 * @param {object[]} pendingMutations  Rows from mutation_log with status='pending'.
 * @returns {object|null} New briefing object with is_read flags adjusted.
 */
export function overlayDaily(briefing, pendingMutations) {
  if (!briefing || !Array.isArray(briefing.themes) || !pendingMutations?.length) return briefing;
  const { reads, unreads } = buildReadOverlay(pendingMutations);
  if (reads.size === 0 && unreads.size === 0) return briefing;
  return {
    ...briefing,
    themes: briefing.themes.map((theme) => ({
      ...theme,
      whats_new: Array.isArray(theme.whats_new)
        ? theme.whats_new.map((a) => applyReadFlag(a, reads, unreads))
        : theme.whats_new,
    })),
  };
}

/**
 * Overlay pending read mutations onto a topic digest payload.
 *
 * @param {object|null} digest  TopicDetail payload (has `.articles[]` and optionally
 *                              `.unread_article_ids[]`).
 * @param {object[]} pendingMutations  Rows from mutation_log with status='pending'.
 * @returns {object|null} New digest object with is_read flags adjusted.
 */
export function overlayTopicDigest(digest, pendingMutations) {
  if (!digest || !pendingMutations?.length) return digest;
  const { reads, unreads } = buildReadOverlay(pendingMutations);
  if (reads.size === 0 && unreads.size === 0) return digest;
  const next = { ...digest };
  if (Array.isArray(digest.articles)) {
    next.articles = digest.articles.map((a) => applyReadFlag(a, reads, unreads));
  }
  if (Array.isArray(digest.unread_article_ids)) {
    // Prune ids that are now marked read. mark_unread doesn't re-add ids the
    // server didn't already know about, so we only filter, never append.
    next.unread_article_ids = digest.unread_article_ids.filter((id) => !reads.has(id));
  }
  return next;
}
